import type { ColumnProps } from '#/common/components/DataGrid';
import { withChip } from '#/common/utils/wrappers';
import { capitalNeedStatusColors } from '../../CapitalNeedDataGrid/columns';

const formatDateOnly = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

type DateFilterValue = string | number | Date;

// This grid's dataSource is a local in-memory array (not a backend query), so `plannedDate`
// is a raw "YYYY-MM-DD" string. The shared DataGrid default date filter compares against a
// full ISO datetime boundary (e.g. "2026-12-31T00:00:00.000Z"), which breaks equality here:
// "2026-12-31" is a string-prefix of that boundary, so plain JS string comparison treats it
// as "less than", excluding exact matches. Build filters using the same date-only format
// the raw values actually use so comparisons line up.
const plannedDateFilterExpression = (value: unknown, operation: string): unknown[] => {
  if (operation === 'between') {
    const [start, end] = (Array.isArray(value) ? value : []) as [DateFilterValue, DateFilterValue];
    const startStr = start ? formatDateOnly(new Date(start)) : undefined;
    const endStr = end ? formatDateOnly(new Date(end)) : undefined;

    if (startStr && endStr) {
      return [['plannedDate', '>=', startStr], 'and', ['plannedDate', '<=', endStr]];
    }
    if (startStr) return ['plannedDate', '>=', startStr];
    if (endStr) return ['plannedDate', '<=', endStr];
    return [];
  }

  const dateStr = formatDateOnly(new Date(value as DateFilterValue));
  return ['plannedDate', operation, dateStr];
};

const columns: ColumnProps<Record<string, string>>[] = [
  {
    dataField: 'title',
    caption: 'Title',
    dataType: 'string',
    visible: true,
    allowFiltering: true,
    allowSearch: true,
    allowSorting: true,
  },
  {
    dataField: 'status',
    caption: 'Status',
    dataType: 'string',
    visible: true,
    allowFiltering: true,
    allowSearch: true,
    allowSorting: true,
    cellRender: (e) => {
      const value = e.value || 'draft';
      const label = e.value ? e.displayValue || '' : 'Draft';
      const customColors = capitalNeedStatusColors[value];
      return withChip(label, 'normal', customColors);
    },
  },
  {
    dataField: 'plannedDate',
    caption: 'Planned Date',
    dataType: 'date',
    visible: true,
    allowFiltering: true,
    allowSearch: true,
    allowSorting: true,
    calcFilterExpression: plannedDateFilterExpression,
  },
  {
    dataField: 'cost',
    caption: 'Planned Cost',
    dataType: 'number',
    format: {
      type: 'currency',
      precision: 2,
    },
    visible: true,
    allowFiltering: true,
    allowSearch: true,
    allowSorting: true,
  },
];

export default columns;
